import { type FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Image from '@segment/matcha/components/Image'
import Typography from '@segment/matcha/components/Typography'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { scrollToAnchor } from '@segment/utils/useScrollToAnchor'

import manageQualityDataPng2x from './assets/ManageQualityData@2x.png'
import manageQualityDataWebp from './assets/ManageQualityData.webp'
import manageQualityDataWebp2x from './assets/ManageQualityData@2x.webp'

const copy = {
  heading: `Manage quality data across all your teams and tools`,
  description: `Help your organization achieve customer data maturity by collecting, governing, and leveraging data effectively at scale.`,
}

const links = [
  {
    text: `Data collection & ETL`,
    href: `data-collection-etl`,
  },
  {
    text: `Data privacy, compliance & governance`,
    href: `data-privacy`,
  },
  {
    text: `Data activation`,
    href: `data-activation`,
  },
]

const data = [
  {
    heading: `Break down data silos across disparate tools and systems`,
    description: `Simplify data integration, eliminating silos and delivering a unified platform for clean, complete, and accurate customer data.`,
  },
  {
    heading: `Maintain compliance with first-party data collection`,
    description: `Respect user privacy and comply with increasingly complex data protection regulations with Segment’s first-party data-centric platform.`,
  },
  {
    heading: `Unlock the data warehouse for all of your teams`,
    description: `Create identity-resolved, real-time customer profiles that automatically sync to your data warehouse streamlining access and integration with your downstream tools.`,
  },
]

const ManageQualityData: FC = () => {
  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading variant="h2" tag="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="pLarge" tag="p">
              {copy.description}
            </CopyText.Description>

            <LinksWrapper>
              {links.map((link, i) => (
                <StyledTypography variant="pExtraLarge" key={link.text}>
                  <CustomDivider>{i === 0 ? null : `|`}</CustomDivider>
                  <a
                    href={`#${link.href}`}
                    onClick={(e) => {
                      e.preventDefault()
                      scrollToAnchor(link.href)
                    }}
                  >
                    {link.text}
                  </a>
                </StyledTypography>
              ))}
            </LinksWrapper>
          </CopyTextBase>
        </Section.Copy>

        <Section.Contents>
          <ColumnsWrapper>
            <ColumnTrack columns={1} overrides={{ md: 2 }}>
              <Content>
                {data.map(({ heading, description }) => (
                  <FeatureBoxBase key={heading} headerPosition="top" alignment="left">
                    <FeatureBox.Body>
                      <FeatureBox.Title>{heading}</FeatureBox.Title>
                      <FeatureBox.Text variant="pMedium">{description}</FeatureBox.Text>
                    </FeatureBox.Body>
                  </FeatureBoxBase>
                ))}
              </Content>

              <picture>
                <source type="image/webp" srcSet={`${manageQualityDataWebp}, ${manageQualityDataWebp2x} 2x`} />
                <StyledImage
                  width="804"
                  height="900"
                  src={manageQualityDataPng2x}
                  alt="Illustration: Manage Quality Data"
                  loading="lazy"
                />
              </picture>
            </ColumnTrack>
          </ColumnsWrapper>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.mint[10]};
`

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 0;
  }
`

const LinksWrapper = styled.div`
  display: inline-flex;
  gap: 12px;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`

const StyledTypography = styled(Typography)`
  display: inline-flex;
  gap: 12px;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin: 0;
  }
`

const CustomDivider = styled.span`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    display: none;
  }
`

const ColumnsWrapper = styled.div`
  margin-top: 30px;
`

const Content = styled.div`
  max-width: 480px;
  justify-self: center;
`

const StyledImage = styled(Image)`
  width: 100%;
  max-width: 400px;
`

export default ManageQualityData
